import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Botao, Cartao, Esqueleto, TituloSecao } from '@/componentes/ui'
import { formatarHorario } from '@/lib/datas'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utilitarios'
import type { LembreteModelo } from '@/tipos/tabelas'
import { useProgramaAtual } from './comum'

/**
 * Modelos dos lembretes diários (seções 9 e 14).
 *
 * Mudar aqui altera o padrão do programa, não o de quem já está no ar:
 * cada participante recebe uma cópia dos horários ao se matricular e
 * pode ajustar os dela no próprio perfil. Sobrescrever esses ajustes a
 * cada edição de texto seria pior do que não ter a tela.
 */
export function SecaoLembretes() {
  const { data: programa } = useProgramaAtual()
  const clienteQuery = useQueryClient()
  const [emEdicao, setEmEdicao] = useState<string | null>(null)
  const [titulo, setTitulo] = useState('')
  const [corpo, setCorpo] = useState('')
  const [horario, setHorario] = useState('')

  const chave = ['admin', 'lembretes-modelo', programa?.id ?? ''] as const

  const { data: modelos = [], isLoading } = useQuery({
    queryKey: chave,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lembretes_modelo')
        .select('*')
        .eq('programa_id', programa?.id ?? '')
        .order('ordem')

      if (error) throw error
      return data as LembreteModelo[]
    },
    enabled: Boolean(programa),
  })

  const salvar = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('lembretes_modelo')
        .update({
          titulo: titulo.trim(),
          corpo: corpo.trim(),
          horario_padrao: `${horario}:00`,
        })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      setEmEdicao(null)
      void clienteQuery.invalidateQueries({ queryKey: chave })
    },
  })

  const alternarAtivo = useMutation({
    mutationFn: async (modelo: LembreteModelo) => {
      const { error } = await supabase
        .from('lembretes_modelo')
        .update({ ativo: !modelo.ativo })
        .eq('id', modelo.id)
      if (error) throw error
    },
    onSuccess: () => void clienteQuery.invalidateQueries({ queryKey: chave }),
  })

  function abrirEdicao(modelo: LembreteModelo) {
    setEmEdicao(modelo.id)
    setTitulo(modelo.titulo)
    setCorpo(modelo.corpo)
    setHorario(formatarHorario(modelo.horario_padrao))
  }

  if (isLoading) return <Esqueleto className="h-64" />

  return (
    <div className="flex flex-col gap-5">
      <Cartao className="bg-dourado/15 text-sm leading-relaxed text-[var(--tinta-suave)]">
        Estes são os <strong className="text-[var(--tinta)]">horários padrão</strong> do programa.
        Quem já está matriculada mantém os horários que escolheu no perfil — editar aqui vale para
        as próximas matrículas.
      </Cartao>

      <TituloSecao>Lembretes diários</TituloSecao>

      <div className="flex flex-col gap-2">
        {modelos.map((modelo) => {
          const editando = emEdicao === modelo.id

          return (
            <Cartao key={modelo.id} className="p-4">
              {editando ? (
                <div className="flex flex-col gap-3">
                  <input
                    type="text"
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    maxLength={60}
                    placeholder="Título"
                    className="h-12 w-full rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] px-3 font-titulo text-[15px] font-semibold"
                  />
                  <textarea
                    rows={2}
                    value={corpo}
                    onChange={(e) => setCorpo(e.target.value)}
                    maxLength={160}
                    placeholder="Mensagem"
                    className="w-full resize-none rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] p-3 text-sm"
                  />
                  <label className="flex items-center gap-3">
                    <span className="text-sm text-[var(--tinta-suave)]">Horário padrão</span>
                    <input
                      type="time"
                      value={horario}
                      onChange={(e) => setHorario(e.target.value)}
                      className="h-11 rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] px-3 font-titulo text-sm font-semibold"
                    />
                  </label>
                  <div className="flex gap-2">
                    <Botao
                      tamanho="pequeno"
                      carregando={salvar.isPending}
                      onClick={() => salvar.mutate(modelo.id)}
                    >
                      Salvar
                    </Botao>
                    <Botao variante="fantasma" tamanho="pequeno" onClick={() => setEmEdicao(null)}>
                      Cancelar
                    </Botao>
                  </div>
                </div>
              ) : (
                <div className={cn('flex items-center gap-3', !modelo.ativo && 'opacity-55')}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={modelo.ativo}
                    aria-label={`${modelo.ativo ? 'Desativar' : 'Ativar'} ${modelo.titulo}`}
                    onClick={() => alternarAtivo.mutate(modelo)}
                    className={cn(
                      // min-h-7 sobrepõe o min-height:44px global (regra
                      // pensada para alvo de toque de botões de texto).
                      // 28×48px já cobre o mínimo de 24px da WCAG 2.2 AA
                      // — o nível exigido aqui, não o AAA de 44px — e
                      // forçar 44 deixava o switch quase um círculo,
                      // maior que o próprio nome do lembrete ao lado.
                      'relative h-7 min-h-7 w-12 shrink-0 rounded-full transition-colors',
                      modelo.ativo ? 'bg-verde' : 'bg-[var(--borda)]',
                    )}
                  >
                    <span
                      className={cn(
                        // left-1 fixo como base: sem isso, a posição de
                        // repouso (antes do translate) usa a resolução
                        // padrão do CSS para left:auto num elemento
                        // absoluto sem irmãos, que diverge entre
                        // motores — no Safari do iOS a bolinha escapava
                        // do pill e caía em cima do texto ao lado.
                        'absolute top-1 left-1 size-5 rounded-full bg-white transition-transform',
                        modelo.ativo ? 'translate-x-5' : 'translate-x-0',
                      )}
                    />
                  </button>

                  <button
                    type="button"
                    onClick={() => abrirEdicao(modelo)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block font-titulo text-[15px] font-semibold">
                        {modelo.titulo}
                      </span>
                      <span className="block truncate text-sm text-[var(--tinta-apagada)]">
                        {modelo.corpo}
                      </span>
                    </span>
                    <span className="shrink-0 font-titulo text-sm font-bold text-coral">
                      {formatarHorario(modelo.horario_padrao)}
                    </span>
                    <span className="shrink-0 text-[var(--tinta-apagada)]" aria-hidden>
                      ✎
                    </span>
                  </button>
                </div>
              )}
            </Cartao>
          )
        })}
      </div>
    </div>
  )
}
